import React, { useState } from "react";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { Form, Input, Button, Tooltip } from "antd";
import {
  HomeOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import {
  signupemailValidationRules,
  textInputValidationRules,
} from "utils/formHelpers/validations";
import { useCreateMember } from "../hooks/useCreateMember";
import { QUERY_KEY_FOR_TEAM_MEMBERS } from "../hooks/useGetMembers";
import { AppButton } from "components/button/AppButton";
import WarningModal from "components/layout/components/WarningModal";
import SuccessModal from "components/layout/components/SuccessModal";

export const AddMemberPage = () => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useCreateMember();
  const [isCancelModalVisible, setCancelModalVisible] = useState(false);
  const [isSuccessModalVisible, setSuccessModalVisible] = useState(false);

  const handleSubmit = (values: any) => {
    mutate(
      {
        ...values,
        token,
      },
      {
        onError: (err: any) => {
          let errorTitle = "Error";
          let errorDescription = "An error occurred, please try again.";

          if (err.response && err.response.data && err.response.data.error) {
            errorTitle = "Error: " + err.response.data.error;
            errorDescription = err.response.data.description || "Please try again.";
          }

          openNotification({
            title: errorTitle,
            state: "error",
            description: errorDescription,
            duration: 4.5,
          });
        },
        onSuccess: (res: any) => {
          queryClient.refetchQueries([QUERY_KEY_FOR_TEAM_MEMBERS]);
          openNotification({
            title: "Success",
            state: "success",
            description: "Team member added successfully",
            duration: 4.5,
          });
          setSuccessModalVisible(true);
        },
      }
    );
  };

  const handleSuccessClose = () => {
    setSuccessModalVisible(false);
    queryClient.refetchQueries([QUERY_KEY_FOR_TEAM_MEMBERS]);
    navigate(routePath.team);
  };

  return (
    <DashboardLayout>
      <Navbar
        title="Add Team Member"
        description="Add a new member to your team"
      />

      {/* Breadcrumb */}
      <div>
        <h3 className="text-sm flex items-center gap-1 text-gray-700">
          <HomeOutlined className="text-gray-500" />
          <span>Team&nbsp;</span>
          <RightOutlined className="text-xs" />
          <span>&nbsp;Add Member</span>
        </h3>
      </div>

      {/* Main Form Content */}
      <div className="bg-white shadow-md rounded-md p-6 mt-6 w-full mx-auto">
        <div className="max-w-2xl mx-auto">
          <p className="mb-6 text-gray-600">
            If the recruiter already exists, they will be added to your team.
            Otherwise, a new recruiter account will be created and added automatically.
          </p>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
            className="space-y-6"
          >
            <Form.Item
              name="name"
              label="Full name"
              rules={textInputValidationRules}
            >
              <Input
                placeholder="Enter full name"
                size="large"
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={signupemailValidationRules(true)}
            >
              <Input
                placeholder="Enter email address"
                size="large"
                className="w-full"
              />
            </Form.Item>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t">
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate(-1)}
                  className="bg-white text-blue-900 rounded border border-gray-300 px-8 h-10 hover:border-gray-400"
                >
                  Back
                </Button>
                <Button
                  onClick={() => setCancelModalVisible(true)}
                  className="h-10 px-8"
                >
                  Cancel
                </Button>
              </div>

              <div>
                <AppButton
                  label="Add Member"
                  type="submit"
                  isLoading={isLoading}
                />
              </div>
            </div>
          </Form>
        </div>
      </div>

      {/* Modals */}
      <WarningModal
        open={isCancelModalVisible}
        title="Cancel progress"
        content="Once cancelled, progress would not be saved."
        onConfirm={() => navigate(routePath.team)}
        onCancel={() => setCancelModalVisible(false)}
        confirmText="Cancel"
        cancelText="Back"
      />

      <SuccessModal
        open={isSuccessModalVisible}
        // title="Success"
        content="Team member added successfully!"
        onClose={handleSuccessClose}
      // confirmText="Go to Team"
      />
    </DashboardLayout>
  );
};